import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { markTutorialPending } from "@/context/TutorialContext";

const AUTH_IMG =
  "https://images.pexels.com/photos/7562087/pexels-photo-7562087.jpeg";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next");
  const dest = nextPath && nextPath.startsWith("/") ? nextPath : "/studio";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(dest, { replace: true });
  }, [user, navigate, dest]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password);
        markTutorialPending(); // trigger onboarding tour on first /studio visit
      }
      toast.success(isLogin ? "Welcome back!" : "Account created!");
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      <Seo
        title={isLogin ? "Sign in" : "Create your account"}
        description={
          isLogin
            ? "Sign in to Nox AI — your all-in-one AI creative studio for images, videos, voiceovers and more."
            : "Create a free Nox AI account and start generating images, videos, voiceovers, music and more in seconds — your all-in-one AI creative studio."
        }
        path={isLogin ? "/login" : "/register"}
        noindex={isLogin}
      />
      {/* Left visual */}
      <div className="relative hidden lg:block">
        <div
          className="absolute inset-0"
          style={{ backgroundImage: `url(${AUTH_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-background/20" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2 w-fit">
            <img src="/nox-logo.png" alt="Nox AI" className="h-9 w-9 object-contain" />
            <span className="font-heading font-black text-lg">
              Nox<span className="text-primary"> AI</span>
            </span>
          </Link>
          <div>
            <h2 className="font-heading text-4xl font-black tracking-tighter leading-tight max-w-md">
              Creative that converts, generated in seconds.
            </h2>
            <p className="mt-4 text-muted-foreground max-w-sm">
              Images and videos, on-brand and campaign-ready. Built for modern marketing teams.
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-10 lg:hidden">
            <img src="/nox-logo.png" alt="Nox AI" className="h-9 w-9 object-contain" />
            <span className="font-heading font-black text-lg">
              Nox<span className="text-primary"> AI</span>
            </span>
          </Link>

          <h1 className="font-heading text-3xl font-black tracking-tight">
            {isLogin ? "Sign in" : "Create your account"}
          </h1>
          <p className="text-muted-foreground mt-2 mb-8">
            {isLogin ? "Welcome back to your creative studio." : "Start generating ad creative today."}
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="auth-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className="bg-[#050508]/50 border-white/10 focus-visible:ring-primary h-11"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {isLogin && (
                  <Link
                    to="/forgot-password"
                    data-testid="forgot-password-link"
                    className="text-xs text-primary hover:underline font-medium"
                  >
                    Forgot password?
                  </Link>
                )}
              </div>
              <Input
                id="password"
                type="password"
                data-testid="password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                className="bg-[#050508]/50 border-white/10 focus-visible:ring-primary h-11"
              />
            </div>

            {error && (
              <p data-testid="auth-error" className="text-sm text-destructive">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-button"
              className="w-full bg-primary text-black hover:bg-[#00C2D9] font-semibold h-11"
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isLogin ? "Sign in" : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground text-center">
            {isLogin ? "New here? " : "Already have an account? "}
            <Link
              to={`${isLogin ? "/register" : "/login"}${nextPath ? `?next=${encodeURIComponent(nextPath)}` : ""}`}
              data-testid="auth-switch-link"
              className="text-primary hover:underline font-medium"
            >
              {isLogin ? "Create an account" : "Sign in"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
